<template>
    <div>
      <b-img src="/images/hayek_logo.png" center></b-img>
      <b-row>
        <b-col md="2"></b-col>
        <b-col md="8">
          <b-card class="mt-3">
            <h3>Reset Your Password</h3>
            <p>Enter the email address associated with your account and the reset code you received.</p>
            <div v-if="successMessage" class="alert alert-success">
              {{ successMessage }}
            </div>
            <div v-if="errorMessage" class="alert alert-danger">
              {{ errorMessage }}
            </div>
            <b-form @submit="onSubmit" @submit.stop.prevent>
              <!-- Email Address -->
              <b-form-group label="Email address:">
                <b-form-input
                  v-model="email"
                  type="email"
                  placeholder="Enter email"
                  :disabled="isCodeVerified"
                  :class="{ 'is-invalid': validation.hasError('email') }"
                  required
                ></b-form-input>
                <div class="invalid-feedback" v-if="validation.hasError('email')">
                  {{ validation.firstError('email') }}
                </div>
              </b-form-group>
  
              <!-- Reset Code -->
              <b-form-group label="Reset Code:" v-if="!isCodeVerified">
                <b-form-input
                  v-model="code"
                  type="text"
                  placeholder="Enter 6-digit reset code"
                  maxlength="6"
                  :class="{ 'is-invalid': validation.hasError('code') }"
                  required
                ></b-form-input>
                <div class="invalid-feedback" v-if="validation.hasError('code')">
                  {{ validation.firstError('code') }}
                </div>
              </b-form-group>
  
              <!-- Password Fields -->
              <div v-if="isCodeVerified">
                <b-form-group label="New Password:">
                  <b-form-input
                    v-model="password"
                    type="password"
                    placeholder="Enter new password"
                    :class="{ 'is-invalid': validation.hasError('password') }"
                    required
                  ></b-form-input>
                  <div class="invalid-feedback" v-if="validation.hasError('password')">
                    {{ validation.firstError('password') }}
                  </div>
                </b-form-group>
  
                <b-form-group label="Confirm New Password:">
                  <b-form-input
                    v-model="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    :class="{ 'is-invalid': validation.hasError('confirmPassword') }"
                    required
                  ></b-form-input>
                  <div class="invalid-feedback" v-if="validation.hasError('confirmPassword')">
                    {{ validation.firstError('confirmPassword') }}
                  </div>
                </b-form-group>
              </div>
  
              <hr />
              <b-row>
                            <b-col>
                                <b-button type="submit" variant="primary" :disabled="isLoading">
                {{ isCodeVerified ? 'Reset Password' : 'Verify Code' }}
              </b-button>
                            </b-col>
                            <b-col>
                                <a href="/" class="btn btn-link float-right">Back to login</a>
                            </b-col>
                        </b-row>              
              
            </b-form>
  
            <!-- Login Link -->
            <div v-if="passwordReset" class="mt-3 text-center">
              <p>Your password has been successfully reset.</p>
              <b-button variant="success" :href="'/'">Go to Login</b-button>
            </div>
          </b-card>
        </b-col>
        <b-col md="2"></b-col>
      </b-row>
    </div>
  </template>
  
  <script>
import { HTTP } from "../../http-common";
import SimpleVueValidation from "simple-vue-validator";

const Validator = SimpleVueValidation.Validator;

export default {
  data() {
    return {
      email: "",
      code: "",
      password: "",
      confirmPassword: "",
      isCodeVerified: false,
      passwordReset: false,
      isLoading: false,
      successMessage: "",
      errorMessage: "",
    };
  },
  mixins: [SimpleVueValidation.mixin], // Attach SimpleVueValidation
  validators() {
    const baseValidators = {
      email: function (value) {
        return Validator.value(value)
          .required("Email is required.")
          .email("Invalid email address.");
      },
      code: function (value) {
        if (!this.isCodeVerified) {
          return Validator.value(value)
            .required("Reset code is required.")
            .length(6, "Reset code must be 6 digits long.");
        }
        return Validator.value(value); // Skip validation if already verified
      },
    };

    // Add password validation only after code is verified
    if (this.isCodeVerified) {
      baseValidators.password = function (value) {
        return Validator.value(value)
          .required("Password is required.")
          .minLength(8, "Password must be at least 8 characters long.")
          .match(/[A-Z]/, "Password must contain at least one uppercase letter.")
          .match(/[a-z]/, "Password must contain at least one lowercase letter.")
          .match(/[0-9]/, "Password must contain at least one number.")
          .match(/[@$!%*?&]/, "Password must contain at least one special character.");
      };
      baseValidators.confirmPassword = function (value) {
        return Validator.value(value)
          .required("Password confirmation is required.")
          .equalTo(this.password, "Passwords do not match.");
      };
    }

    return baseValidators;
  },
  methods: {
    async onSubmit(event) {
      event.preventDefault();

      this.$validate().then(async (success) => {
        if (!success) {
          return; // Stop submission if validation fails
        }

        this.isLoading = true;
        this.successMessage = "";
        this.errorMessage = "";

        try {
          if (!this.isCodeVerified) {
            // Verify Reset Code
            await HTTP.post("/auth/verify-code", { email: this.email, code: this.code });
            this.isCodeVerified = true;
            this.successMessage = "Reset code verified. Please enter a new password.";
          } else {
            // Submit New Password
            await HTTP.put("/auth/reset-password", { email: this.email, password: this.password });
            this.passwordReset = true;
            this.successMessage = "Your password has been successfully reset.";
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            this.errorMessage = "Invalid reset code or email. Please try again.";
          } else {
            this.errorMessage = "An error occurred. Please try again later.";
          }
        } finally {
          this.isLoading = false;
        }
      });
    },
  },
};
</script>

  
  <style scoped>
  .invalid-feedback {
    display: block;
  }
  </style>
  